import {
  buildAuthUrl,
  buildRefreshBody,
  buildTokenExchangeBody,
  deriveCodeChallenge,
  generateCodeVerifier,
  generateState,
  parseAuthCallback,
  type IdentityProvider,
  type IdentitySession,
  type TokenResponse,
} from "@roqeet/core";

/**
 * Google OAuth(PKCE) 신원 제공자 — 결정 #8(클라이언트 사이드, 서버 불필요).
 *
 * 익명 사용자가 100% 기능을 쓰고, Google 연결은 **순전히 본인 Drive appDataFolder**에
 * 백업하기 위한 선택지다(우리 서버는 데이터를 보지 않는다). PKCE라 client secret 없이
 * 브라우저에서 안전하게 authorization code 흐름을 수행한다.
 *
 * 플로우(팝업):
 *  1) code_verifier/state 생성 → sessionStorage에 보관.
 *  2) 팝업으로 Google 동의 화면(buildAuthUrl)을 연다.
 *  3) 팝업이 redirect_uri(앱 자신)로 돌아오면, 콜백 감지 코드가 code를 opener로
 *     postMessage하고 팝업을 닫는다(아래 handleOAuthRedirectIfPopup).
 *  4) code + verifier를 토큰 엔드포인트에 교환 → 세션 저장.
 *
 * 토큰 보관(결정 #15의 정직성): 순수 클라이언트 앱은 완벽히 안전한 저장소가 없다.
 * 세션은 localStorage에 두되, Drive 범위로만 한정하고 동기화에는 절대 포함하지 않는다.
 */

const GOOGLE_AUTH_ENDPOINT = "https://accounts.google.com/o/oauth2/v2/auth";
const GOOGLE_TOKEN_ENDPOINT = "https://oauth2.googleapis.com/token";
const GOOGLE_USERINFO_ENDPOINT = "https://www.googleapis.com/oauth2/v3/userinfo";
/** Drive appDataFolder 범위(앱 전용 폴더만 — 사용자의 다른 파일엔 접근 불가). */
const DRIVE_APPDATA_SCOPE = "https://www.googleapis.com/auth/drive.appdata";
const DEFAULT_SCOPES = [DRIVE_APPDATA_SCOPE, "openid", "email", "profile"];

const SESSION_KEY = "roqeet.identity.google";
const PKCE_VERIFIER_KEY = "roqeet.identity.pkce.verifier";
const PKCE_STATE_KEY = "roqeet.identity.pkce.state";
/** 팝업→opener 메시지 식별자. */
const OAUTH_MESSAGE_TYPE = "roqeet-oauth-callback";
/** localStorage key used as a fallback channel when window.opener is lost. */
const OAUTH_LS_CALLBACK_KEY = "roqeet.oauth.callback";

interface StoredSession {
  tier: "google";
  accessToken: string;
  refreshToken?: string;
  expiresAt?: number;
  displayName?: string;
  email?: string;
  avatarUrl?: string;
  /**
   * OpenID Connect id_token(JWT). RoQeet Hub(결정 #31)가 Supabase GoTrue 로그인에
   * 재사용한다. Drive 동기화(결정 #8)에는 쓰이지 않으며, 동기화 페이로드에는 절대
   * 포함되지 않는다(결정 #15: 토큰은 기기 로컬).
   */
  idToken?: string;
}

const ANONYMOUS: IdentitySession = { tier: "anonymous" };

/** 토큰 응답(+선택적 프로필)을 저장 세션 형태로 환산한다(순수 — 테스트 가능). */
export function sessionFromTokenResponse(
  token: TokenResponse,
  now: number,
  profile?: { name?: string; email?: string; picture?: string },
  previousRefreshToken?: string,
): StoredSession {
  return {
    tier: "google",
    accessToken: token.access_token,
    // refresh_token은 첫 동의에서만 오는 경우가 많다 — 없으면 이전 것을 유지.
    ...((token.refresh_token ?? previousRefreshToken)
      ? { refreshToken: token.refresh_token ?? previousRefreshToken }
      : {}),
    ...(token.expires_in ? { expiresAt: now + token.expires_in * 1000 } : {}),
    ...(token.id_token ? { idToken: token.id_token } : {}),
    ...(profile?.name ? { displayName: profile.name } : {}),
    ...(profile?.email ? { email: profile.email } : {}),
    ...(profile?.picture ? { avatarUrl: profile.picture } : {}),
  };
}

/** 저장 세션 → 공개 IdentitySession. */
function toPublicSession(stored: StoredSession | null): IdentitySession {
  if (!stored) return ANONYMOUS;
  return {
    tier: "google",
    accessToken: stored.accessToken,
    ...(stored.expiresAt ? { expiresAt: stored.expiresAt } : {}),
    ...(stored.displayName ? { displayName: stored.displayName } : {}),
    ...(stored.email ? { email: stored.email } : {}),
    ...(stored.avatarUrl ? { avatarUrl: stored.avatarUrl } : {}),
  };
}

function readStored(): StoredSession | null {
  try {
    const raw = localStorage.getItem(SESSION_KEY);
    return raw ? (JSON.parse(raw) as StoredSession) : null;
  } catch {
    return null;
  }
}

function writeStored(session: StoredSession | null): void {
  try {
    if (session) localStorage.setItem(SESSION_KEY, JSON.stringify(session));
    else localStorage.removeItem(SESSION_KEY);
  } catch {
    /* private mode: 세션 비영속(연결은 이번 탭에서만 유효) */
  }
}

/**
 * 팝업 컨텍스트에서 OAuth 콜백을 감지해 opener로 code를 전달하고 닫는다(결정 #8).
 * `main.tsx`가 렌더 전에 호출한다. 콜백이 아니면 아무 일도 하지 않는다.
 *
 * @returns 콜백을 처리해 창을 닫는 중이면 true(앱 렌더를 건너뛰어야 함).
 */
export function handleOAuthRedirectIfPopup(): boolean {
  if (typeof window === "undefined") return false;
  const { code, state, error } = parseAuthCallback(window.location.search);
  if (!code && !error) return false;

  const payload = JSON.stringify({ type: OAUTH_MESSAGE_TYPE, code, state, error });

  // Primary path: postMessage to opener.
  let posted = false;
  if (window.opener) {
    try {
      window.opener.postMessage(
        { type: OAUTH_MESSAGE_TYPE, code, state, error },
        "*",
      );
      posted = true;
    } catch {
      /* opener가 사라졌거나 cross-origin: 무시 — localStorage 폴백 사용 */
    }
  }

  // Fallback: write to localStorage so parent window can pick it up via
  // the 'storage' event. This covers Cloudflare Pages / PWA scenarios
  // where window.opener is null after a full-page redirect in the popup.
  try {
    localStorage.setItem(OAUTH_LS_CALLBACK_KEY, payload);
  } catch {
    /* private mode 등 — 무시 */
  }

  // Clean the URL query so a refresh doesn't re-trigger.
  try {
    window.history.replaceState({}, "", window.location.pathname);
  } catch { /* ignore */ }

  setTimeout(() => window.close(), 100);
  return true;
}

export class GoogleIdentityProvider implements IdentityProvider {
  private session: IdentitySession;
  private readonly listeners = new Set<(s: IdentitySession) => void>();
  private readonly clientId: string;
  private readonly clientSecret: string;
  private readonly redirectUri: string;

  constructor(clientId?: string, redirectUri?: string) {
    this.clientId = clientId ?? import.meta.env.VITE_GOOGLE_CLIENT_ID ?? "";
    this.clientSecret = import.meta.env.VITE_GOOGLE_CLIENT_SECRET ?? "";
    this.redirectUri =
      redirectUri ??
      import.meta.env.VITE_GOOGLE_REDIRECT_URI ??
      (typeof window !== "undefined" ? window.location.origin + window.location.pathname : "");
    this.session = toPublicSession(readStored());
  }

  /** Google 연결이 구성됐는지(클라이언트 ID 존재). */
  isConfigured(): boolean {
    return this.clientId.length > 0;
  }

  current(): IdentitySession {
    return this.session;
  }

  subscribe(onChange: (session: IdentitySession) => void): () => void {
    this.listeners.add(onChange);
    onChange(this.session);
    return () => this.listeners.delete(onChange);
  }

  private emit(session: IdentitySession): void {
    this.session = session;
    for (const fn of this.listeners) fn(session);
  }

  async connectGoogle(scopes: string[] = DEFAULT_SCOPES): Promise<IdentitySession> {
    if (!this.isConfigured()) {
      throw new Error(
        "Google 클라이언트 ID가 설정되지 않았습니다. VITE_GOOGLE_CLIENT_ID를 빌드에 주입하세요.",
      );
    }
    const verifier = generateCodeVerifier();
    const state = generateState();
    const challenge = await deriveCodeChallenge(verifier);
    sessionStorage.setItem(PKCE_VERIFIER_KEY, verifier);
    sessionStorage.setItem(PKCE_STATE_KEY, state);

    const authUrl = buildAuthUrl({
      authEndpoint: GOOGLE_AUTH_ENDPOINT,
      clientId: this.clientId,
      redirectUri: this.redirectUri,
      scopes,
      codeChallenge: challenge,
      state,
      // offline+consent로 refresh_token을 확보(이후 무중단 갱신).
      extra: { access_type: "offline", prompt: "consent" },
    });

    const code = await this.runPopupFlow(authUrl, state);
    const token = await this.exchangeCode(code, verifier);
    const profile = await this.fetchProfile(token.access_token).catch(() => undefined);
    const stored = sessionFromTokenResponse(token, Date.now(), profile);
    writeStored(stored);
    const pub = toPublicSession(stored);
    this.emit(pub);
    return pub;
  }

  /** 팝업을 열고 콜백 postMessage로 code를 회수한다(CSRF state 검증 포함). */
  private runPopupFlow(authUrl: string, expectedState: string): Promise<string> {
    // Clear any stale callback from a previous attempt.
    try { localStorage.removeItem(OAUTH_LS_CALLBACK_KEY); } catch { /* ignore */ }

    return new Promise<string>((resolve, reject) => {
      const popup = window.open(authUrl, "roqeet-google-oauth", "width=480,height=640");
      if (!popup) {
        reject(new Error("팝업이 차단되었습니다. 팝업을 허용한 뒤 다시 시도하세요."));
        return;
      }
      let settled = false;

      const handlePayload = (data: { type?: string; code?: string; state?: string; error?: string }) => {
        if (settled) return;
        if (data?.type !== OAUTH_MESSAGE_TYPE) return;
        settled = true;
        cleanup();
        if (data.error) {
          reject(new Error(`Google 인증 실패: ${data.error}`));
        } else if (data.state !== expectedState) {
          reject(new Error("인증 상태(state) 불일치 — 보안상 거부합니다."));
        } else if (!data.code) {
          reject(new Error("인증 코드를 받지 못했습니다."));
        } else {
          resolve(data.code);
        }
      };

      const cleanup = () => {
        window.removeEventListener("message", onMessage);
        window.removeEventListener("storage", onStorage);
        clearInterval(closedTimer);
        try { localStorage.removeItem(OAUTH_LS_CALLBACK_KEY); } catch { /* ignore */ }
      };

      // Channel 1: postMessage from popup (works when window.opener is alive).
      const onMessage = (e: MessageEvent) => {
        handlePayload(e.data as { type?: string; code?: string; state?: string; error?: string });
      };

      // Channel 2: localStorage 'storage' event (fallback for when window.opener
      // is lost, e.g. Cloudflare Pages / PWA full-page navigation in popup).
      const onStorage = (e: StorageEvent) => {
        if (e.key !== OAUTH_LS_CALLBACK_KEY || !e.newValue) return;
        try {
          handlePayload(JSON.parse(e.newValue) as { type?: string; code?: string; state?: string; error?: string });
        } catch { /* malformed JSON — ignore */ }
      };

      window.addEventListener("message", onMessage);
      window.addEventListener("storage", onStorage);

      // 사용자가 팝업을 직접 닫은 경우 처리.
      // Also check localStorage one last time in case the storage event fired
      // before our listener was set up, or the popup wrote and closed very fast.
      const closedTimer = setInterval(() => {
        if (popup.closed && !settled) {
          // One final check: maybe the popup wrote to localStorage before closing.
          try {
            const raw = localStorage.getItem(OAUTH_LS_CALLBACK_KEY);
            if (raw) {
              handlePayload(JSON.parse(raw) as { type?: string; code?: string; state?: string; error?: string });
              if (settled) return; // handlePayload resolved/rejected
            }
          } catch { /* ignore */ }
          cleanup();
          reject(new Error("인증 창이 닫혔습니다."));
        }
      }, 500);
    });
  }

  private async exchangeCode(code: string, verifier: string): Promise<TokenResponse> {
    const res = await fetch(GOOGLE_TOKEN_ENDPOINT, {
      method: "POST",
      headers: { "content-type": "application/x-www-form-urlencoded" },
      body: buildTokenExchangeBody({
        clientId: this.clientId,
        clientSecret: this.clientSecret || undefined,
        redirectUri: this.redirectUri,
        code,
        codeVerifier: verifier,
      }),
    });
    if (!res.ok) {
      throw new Error(`토큰 교환 실패(${res.status}): ${await res.text()}`);
    }
    return (await res.json()) as TokenResponse;
  }

  private async fetchProfile(
    accessToken: string,
  ): Promise<{ name?: string; email?: string; picture?: string }> {
    const res = await fetch(GOOGLE_USERINFO_ENDPOINT, {
      headers: { authorization: `Bearer ${accessToken}` },
    });
    if (!res.ok) return {};
    return (await res.json()) as { name?: string; email?: string; picture?: string };
  }

  async refresh(): Promise<IdentitySession> {
    const stored = readStored();
    if (!stored?.refreshToken || !this.isConfigured()) {
      // 갱신 불가 — 현재(만료됐을 수 있는) 세션 그대로 반환. 호출부가 재연결 유도.
      return this.session;
    }
    const res = await fetch(GOOGLE_TOKEN_ENDPOINT, {
      method: "POST",
      headers: { "content-type": "application/x-www-form-urlencoded" },
      body: buildRefreshBody(this.clientId, stored.refreshToken, this.clientSecret || undefined),
    });
    if (!res.ok) {
      // 리프레시 실패(취소/만료) → 익명으로 강등하고 재연결 유도.
      writeStored(null);
      this.emit(ANONYMOUS);
      throw new Error(`토큰 갱신 실패(${res.status})`);
    }
    const token = (await res.json()) as TokenResponse;
    const next = sessionFromTokenResponse(token, Date.now(), undefined, stored.refreshToken);
    // 프로필 정보는 유지.
    if (stored.displayName) next.displayName = stored.displayName;
    if (stored.email) next.email = stored.email;
    if (stored.avatarUrl) next.avatarUrl = stored.avatarUrl;
    // refresh 응답에 id_token이 없으면 이전 것을 유지(허브 로그인 재사용, 결정 #31).
    if (!next.idToken && stored.idToken) next.idToken = stored.idToken;
    writeStored(next);
    const pub = toPublicSession(next);
    this.emit(pub);
    return pub;
  }

  /**
   * 유효한 access token을 반환한다(만료 임박 시 자동 갱신). Drive 백엔드가 호출한다.
   * 갱신 불가/미연결이면 undefined.
   */
  async getValidAccessToken(): Promise<string | undefined> {
    const stored = readStored();
    if (!stored) return undefined;
    // 만료 60초 전이면 미리 갱신.
    if (stored.expiresAt && stored.expiresAt - Date.now() < 60_000 && stored.refreshToken) {
      try {
        const refreshed = await this.refresh();
        return refreshed.accessToken;
      } catch {
        return undefined;
      }
    }
    return stored.accessToken;
  }

  /**
   * Google id_token(JWT)을 반환한다(RoQeet Hub 로그인용, 결정 #31). Supabase GoTrue가
   * 이 토큰으로 신원을 검증한다. 없으면(예: openid 스코프 미요청, 만료) undefined —
   * 호출부는 connectGoogle로 재연결을 유도한다.
   */
  getIdToken(): string | undefined {
    return readStored()?.idToken;
  }

  async disconnect(): Promise<void> {
    writeStored(null);
    this.emit(ANONYMOUS);
  }
}
